import {
  BoxProps,
  Drawer,
  DrawerBodyProps,
  DrawerContentProps,
  DrawerHeaderProps,
  DrawerOverlayProps,
  DrawerProps,
  DrawerTitleProps,
  Group,
  GroupProps,
  Tooltip,
  ActionIcon,
  Box,
} from '@mantine/core';
import { Breakpoints } from '@vision/ui/constants';
import { DrawerConfig } from '@vision/theme';
import { useMediaQuery } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import {
  faArrowDownLeftAndArrowUpRightToCenter,
  faArrowUpRightAndArrowDownLeftFromCenter,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classes from './VDrawer.module.css';
import clsx from 'clsx';
import React, { useState } from 'react';

interface VDrawerRootProps extends DrawerProps {
  canFullscreen?: boolean;
  children: React.ReactNode;
}

function VDrawerRoot({ children, ...props }: React.PropsWithChildren<VDrawerRootProps>) {
  const matches = useMediaQuery(Breakpoints.DOWN.SM);
  const defaultSize = DrawerConfig.defaultProps.size;
  const calculatedSize = matches ? '100%' : defaultSize;

  return (
    <Drawer.Root position="right" size={calculatedSize} {...props}>
      {children}
    </Drawer.Root>
  );
}

interface VDrawerOverlayProps extends DrawerOverlayProps {}

function VDrawerOverlay({ ...props }: React.PropsWithChildren<VDrawerOverlayProps>) {
  return <Drawer.Overlay {...DrawerConfig.defaultProps.overlayProps} {...props} />;
}

interface VDrawerContentProps extends DrawerContentProps {
  children: React.ReactNode;
}

function VDrawerContent({ children, ...props }: React.PropsWithChildren<VDrawerContentProps>) {
  return <Drawer.Content {...props}>{children}</Drawer.Content>;
}

interface VDrawerHeaderProps extends DrawerHeaderProps {
  children: React.ReactNode;
  showFullScreenButton?: boolean;
  fullScreen?: boolean;
  onFullScreenToggle?: () => void;
}

interface VDrawerFullScreenButtonProps {
  fullScreen?: boolean;
  onClick?: () => void;
}

function VDrawerFullScreenButton({ fullScreen, onClick }: VDrawerFullScreenButtonProps) {
  const { t } = useTranslation();

  return (
    <Tooltip label={fullScreen ? t('exitFullScreen') : t('fullScreen')} multiline={false}>
      <ActionIcon onClick={onClick} variant="subtle">
        {fullScreen ? (
          <FontAwesomeIcon icon={faArrowDownLeftAndArrowUpRightToCenter} size="xs" />
        ) : (
          <FontAwesomeIcon icon={faArrowUpRightAndArrowDownLeftFromCenter} size="xs" />
        )}
      </ActionIcon>
    </Tooltip>
  );
}

function VDrawerHeader({
  children,
  showFullScreenButton,
  fullScreen,
  onFullScreenToggle,
  ...props
}: React.PropsWithChildren<VDrawerHeaderProps>) {
  const { t } = useTranslation();

  return (
    <Drawer.Header {...props} className={clsx(classes.header, props.className)}>
      {children}
      <Group gap={10}>
        {showFullScreenButton && <VDrawerFullScreenButton fullScreen={fullScreen} onClick={onFullScreenToggle} />}
        <Tooltip label={t('close')}>
          <Drawer.CloseButton className={classes.closeButton} />
        </Tooltip>
      </Group>
    </Drawer.Header>
  );
}

interface VDrawerTitleProps extends DrawerTitleProps {
  children: React.ReactNode;
}

function VDrawerTitle({ children, ...props }: React.PropsWithChildren<VDrawerTitleProps>) {
  return <Drawer.Title {...props}>{children}</Drawer.Title>;
}

interface VDrawerBodyProps extends DrawerBodyProps {
  children: React.ReactNode;
}

function VDrawerBody({ children, className, ...props }: React.PropsWithChildren<VDrawerBodyProps>) {
  return (
    <Drawer.Body {...props} className={clsx(classes.body, className)}>
      {children}
    </Drawer.Body>
  );
}

interface VDrawerFooterProps extends BoxProps {
  bottomContent?: React.ReactNode;
  justify?: GroupProps['justify'];
  topContent?: React.ReactNode;
}

function VDrawerFooter({
  children,
  bottomContent,
  justify = 'right',
  topContent,
  ...props
}: React.PropsWithChildren<VDrawerFooterProps>) {
  return (
    <Box {...props} className={clsx(classes.footer, props.className)}>
      {topContent && <div className={classes.topContent}>{topContent}</div>}
      <Group justify={justify} gap={10} className={classes.content}>
        {children}
      </Group>
      {bottomContent && <div className={classes.bottomContent}>{bottomContent}</div>}
    </Box>
  );
}

interface VDrawerProps extends VDrawerRootProps {
  children: React.ReactNode;
}

export function VDrawer({ size, title, canFullscreen, children, ...props }: VDrawerProps) {
  const matches = useMediaQuery(Breakpoints.DOWN.SM);
  const defaultSize = DrawerConfig.defaultProps.size;
  const [fullScreen, setFullScreen] = useState(false);

  const calculatedSize = fullScreen ? '100%' : matches ? '100%' : size || defaultSize;

  return (
    <VDrawerRoot
      position={DrawerConfig.defaultProps.position}
      className={clsx(classes.vDrawer, props.className)}
      {...props}
      size={calculatedSize}
    >
      <VDrawerOverlay />
      <VDrawerContent>
        <VDrawerHeader
          showFullScreenButton={canFullscreen}
          fullScreen={fullScreen}
          onFullScreenToggle={() => setFullScreen(!fullScreen)}
        >
          <VDrawerTitle>{title}</VDrawerTitle>
        </VDrawerHeader>
        <VDrawerBody className={classes.body}>{children}</VDrawerBody>
      </VDrawerContent>
    </VDrawerRoot>
  );
}

VDrawer.Root = VDrawerRoot;
VDrawer.Overlay = VDrawerOverlay;
VDrawer.Content = VDrawerContent;
VDrawer.Header = VDrawerHeader;
VDrawer.Title = VDrawerTitle;
VDrawer.Body = VDrawerBody;
VDrawer.Footer = VDrawerFooter;
VDrawer.FullscreenButton = VDrawerFullScreenButton;
